import React, { useMemo  } from 'react';
import {useSelector} from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DmLink from '../../UI/DmLink/DmLink';
import DmSignal from '../../UI/DmSignal/DmSignal';
import PressureSensorPlc from '../../UI/PressureSensorPlc/PressureSensorPlc';
import PressureSensorPch from '../../UI/PressureSensorPch/PressureSensorPch';
import { checkBit, checkConnectionSlave, checkExistNA, parseBits, stateNA, formatParametr } from '../../Utils/Functions';

const RsSummary = React.memo (({item, islink, browserTime})=>{
    const role = useSelector((state) => state.userdata?.role);
    const navigate = useNavigate();

  // Основная логика подготовки данных
  const preparedData = useMemo(() => {
    if (!item) return null;

    const baseInfo = {
        typeRsDevice_name: item?.typeRsDevice_name || '...',
        imei: item?.imei || '...',
        imei_name: item?.imei_name || '...',
        param_last_date: item?.param_last_date || 0,
        username: item?.username || '...',
        signalValue: item?.Sig_Value || '...',
    };

    const isConnectionLost = checkConnectionSlave(item);
    const deviceType = baseInfo.typeRsDevice_name;

    // Обработка PLC устройств (до 4 насосов)
    const processPLCDevice = () => {
        const pumps = [];
        
        for (let i = 1; i <= 4; i++) {
            const paramIndex = (i - 1) * 3 + 5;
            const existParam = item?.[`param${paramIndex}`];
            
            if (!checkExistNA(existParam)) continue;

            const modeBitIndex = i - 1;
            const modeWork = checkBit(item?.param4, modeBitIndex) ? 'Автомат' : 'Ручной';
            
            const parsedBits = parseBits(parseInt(existParam));
            const state = parsedBits ? stateNA(parsedBits.bits0to3) : '...';
            const speed = formatParametr(item?.[`param${paramIndex + 2}`]);

            pumps.push({
                name: `НА${i}`,
                modeWork,
                state,
                speed,
            });
        }

        return pumps;
    };

    // Обработка PCH устройств
    const processPCHDevice = () => {
        const getModeWork = () => {
            switch (deviceType) {
                case 'PCH_ATV':
                    switch (item?.param8) {
                        case '1': return 'по ПИД';
                        case '0': return 'по Скорости';
                        default: return '...';
                    }
                case 'PCH_Instart': 
                    switch (item?.param8) {
                        case '8': return 'по ПИД';
                        case '9': return 'по Скорости';
                        default: return '...';
                    }
                case 'PCH_ESQ': 
                    switch (item?.param8) {
                        case '8': return 'по ПИД';
                        case '9': return 'по Скорости';
                        default: return '...';
                    }

                    default: return '...';       
            }
        };
    
        const getState = () => {
            if (deviceType === 'PCH_ATV') {
                if (checkBit(item?.param6, 1)) return 'Готов к работе';
                if (checkBit(item?.param6, 2)) return 'В работе';
                if (checkBit(item?.param6, 3)) return 'Авария';
                return '...';
            } else {
                const state = (item?.param12 === '65535' || item?.param12 === '0') ?
                stateNA(item?.param6) : 'Авария';
                return state;
            }
        };
    
        const getSpeed = () => {
            return formatParametr(item?.param9, deviceType === 'PCH_ATV' ? 1 : 2);
        };
    
        return [{
            name: 'НА',
            modeWork: getModeWork(),
            state: getState(),
            speed: getSpeed(),
        }];
    };

    const pumps = deviceType === 'PLC' 
        ? processPLCDevice() 
        : ['PCH_Instart', 'PCH_ESQ', 'PCH_ATV'].includes(deviceType) 
        ? processPCHDevice() 
        : [];

    const pressureData = deviceType === 'PLC' ? {
        pressure: formatParametr(item?.param1, 1, 'Бар'),
        fault: checkBit(item?.param4, 15),
        islink
    } : { //если ПЧ пересчет в компоненте
        percent: item?.param1,
        pmax: item?.PMax,
        islink
    };

    return {
        ...baseInfo,
        isConnectionLost,
        pumps,
        pressureData,
        deviceType
    };
}, [item]);

const handleClick = (e) => {
    e.preventDefault();
    navigate(`/device/${item.imei}`);
};

if (!preparedData) return null;

return (
    <section className='cardPivot' onClick={handleClick}>
        <div className='cardHeader'> 
            <div className='cardHeaderInfo'> 
                <div className='cardHeaderInfoP'> 
              
                    {!preparedData.isConnectionLost && 
                    preparedData.deviceType === 'PLC' && preparedData.pressureData && (
                        <PressureSensorPlc 
                            pressure={preparedData.pressureData.pressure}
                            fault={preparedData.pressureData.fault}
                            islink={preparedData.pressureData.islink} 
                        />
                    )}
                    {!preparedData.isConnectionLost &&
                    ['PCH_Instart', 'PCH_ESQ', 'PCH_ATV'].includes(preparedData.deviceType) && (
                        <PressureSensorPch 
                            percent={preparedData.pressureData.percent}
                            pmax={preparedData.pressureData.pmax}
                            islink={preparedData.pressureData.islink} 
                        />
                    )}  
                </div>
            </div>
            <div className='cardHeaderLink'>
                <DmSignal 
                    signalValue={preparedData.signalValue} 
                    param_last_date={preparedData.param_last_date}
                    browserTime = {browserTime} 
                />             
                <DmLink islink={islink} />
            </div>
        </div>
        
        <div className='cardInfoNameRs'>  
            <h3>{preparedData.imei_name}</h3>
        </div>
        
        <div className="cardBodyRS">  
            <table className="pump-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>Режим</th>
                        <th>Состояние</th>
                        <th>Скорость</th>
                    </tr>
                </thead>
                <tbody>
                    {islink === 'Offline' ? ( 
                        <tr>
                            <td className="pump-name"></td>
                            <td className="status-warning" colSpan="3">
                                <h3 className='noData'>Нет данных</h3>
                            </td>
                        </tr>
                    ) : ( 
                        preparedData.isConnectionLost ? (
                            <tr>
                                <td className="pump-name"></td>
                                <td className="statusNA-error" colSpan="3">
                                    <h3 className='isConnectionLost'>
                                        Нет связи с {preparedData.deviceType === 'PLC' ? 'ПЛК' : 'ПЧ'}
                                    </h3>
                                </td>
                            </tr>
                        ) : (
                            preparedData.pumps.map(pump => (
                                <tr key={pump.name}>
                                    <td className="pump-name">{pump.name}</td>
                                    <td>{pump.modeWork}</td>
                                    <td className={pump.state=== 'Авария' ? ("statusNA-error") :
                                    (pump.state==='В работе') ? ("statusNA-active"):("statusNA-warning")
                                    }>
                                        {pump.state}
                                    </td>
                                    <td>{pump.speed}</td>
                                </tr>
                            ))
                        )
                    )}
                </tbody>
            </table>
        </div>
        
        <div className='cardFooter'>
            {role === 1 && (
                <span>
                    {`User: ${preparedData.username}, imei: ${preparedData.imei}, device: ${preparedData.deviceType}`}
                </span>
            )}
        </div>
    </section>
);
});
export default RsSummary;